import React, { useState, useEffect, useCallback } from 'react';
import { ButtonBase } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Spinner, Tables } from '../../../../../../Components';
import { TableActions } from '../../../../../../Enums';
import {
  GetAllPaymentPlansByUnitId,
  UpdateUnitPaymentPlanApi,
} from '../../../../../../Services';
import {
  GetParams,
  bottomBoxComponentUpdate,
  showSuccess,
  showError,
} from '../../../../../../Helper';
import {
  PaymentPlanManagementDialog,
  PaymentPlanDeleteDialog,
} from './PaymentPlanViewUtilities';
import { LeadOwnerUnitSalePermissions } from '../../../../../../Permissions';
import { getIsAllowedPermission } from '../../../../../../Helper/Permissions.Helper';
import { PaymentPlanNotAvailableDialog } from '../../../../../../Components/PaymentPlanNotAvailableDialog';
import './UnitProfilePaymentPlanComponent.scss';

const ALLOWED_EDIT_PATHS = [
  '/home/units-sales/unit-profile-edit',
  '/home/units-lease/unit-profile-edit',
  '/home/lead-owner-units-sale/unit-profile-edit',
];

const INITIAL_FILTER = {
  pageSize: 25,
  pageIndex: 0,
};

export const UnitProfilePaymentPlanComponent = ({
  parentTranslationPath,
  translationPath,
}) => {
  const unitId = +GetParams('id');
  const { t } = useTranslation(parentTranslationPath);
  const loginResponse = useSelector((state) => state.login.loginResponse);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentPlans, setPaymentPlans] = useState({
    result: [],
    totalCount: 0,
  });
  const [checkedCardsIds, setCheckedCardsIds] = useState([]);
  const [checkedCards, setCheckedCards] = useState([]);
  const [filter, setFilter] = useState({ ...INITIAL_FILTER, unitId });
  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [
    isPaymentPlanNotAvailableDialogOpen,
    setIsPaymentPlanNotAvailableDialogOpen,
  ] = useState(false);

  const [activeItem, setActiveItem] = useState(null);
  const [currentRowClicked, setCurrentRowClicked] = useState(false);
  const isCorrectLocation = useCallback(() => {
    return ALLOWED_EDIT_PATHS.includes(window.location.pathname);
  }, []);

  const getAllPaymentTypes = useCallback(async (id) => {
    setIsLoading(true);
    try {
      const res = await GetAllPaymentPlansByUnitId(id);
      if (res && (!res.status || res.status === 200)) {
        setPaymentPlans({
          result: res || [],
          totalCount: res.totalCount || 0,
        });
      } else {
        setPaymentPlans({ result: [], totalCount: 0 });
      }
    } catch (error) {
      console.error('Error fetching payment plans:', error);
      setPaymentPlans({ result: [], totalCount: 0 });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateUnitPaymentPlanApi = useCallback(async () => {
    setIsLoading(true);
    try {
      const unitPaymentPlansList = paymentPlans.result.map((item) => {
        const isItemChecked = checkedCardsIds.includes(
          item.propertyPaymentPlanId
        );
        return {
          propertyPaymentPlanId: item.propertyPaymentPlanId,
          isAdded: isItemChecked || item.isSelected,
        };
      });

      if (unitPaymentPlansList.length > 0) {
        const body = { unitPaymentPlans: unitPaymentPlansList };
        const res = await UpdateUnitPaymentPlanApi(unitId, body);

        if (res && (!res.status || res.status === 200)) {
          showSuccess(t('payment-plan-saved-successfully'));
        } else {
          showError(t('failed-to-save-payment-plan'));
        }
      }
    } catch (error) {
      console.error('Error updating payment plan:', error);
      showError(t('failed-to-save-payment-plan'));
    } finally {
      setIsLoading(false);
    }
  }, [paymentPlans.result, checkedCardsIds, unitId, t]);

  const tableActionClicked = useCallback(
    (actionEnum, item, focusedRow, event) => {
      event.stopPropagation();
      event.preventDefault();
      if (actionEnum === TableActions.deleteText.key) {
        setOpenConfirmDialog(true);
        setActiveItem(item);
      } else if (actionEnum === TableActions.editText.key) {
        setActiveItem(item);
        setOpenDialog(true);
      }
    },
    []
  );

  const updateSelectedCards = useCallback((row) => {
    const paymentPlanId = row.propertyPaymentPlanId;

    setCheckedCardsIds((prev) => {
      const index = prev.indexOf(paymentPlanId);
      return index !== -1
        ? prev.filter((id) => id !== paymentPlanId)
        : [...prev, paymentPlanId];
    });

    setCheckedCards((prev) => {
      const index = prev.findIndex(
        (item) => item.propertyPaymentPlanId === paymentPlanId
      );
      return index !== -1
        ? prev.filter((item) => item.propertyPaymentPlanId !== paymentPlanId)
        : [...prev, row];
    });
  }, []);

  const onSelectClicked = useCallback(
    (row, rowIndex) => {
      setActiveItem(row);

      if (row?.allowSelectPaymentPlan === false) {
        setIsPaymentPlanNotAvailableDialogOpen(true);
        return;
      }

      if (!row.isSelectedUnit) {
        const updatedPlans = [...paymentPlans.result];
        updatedPlans[rowIndex].isSelectedUnit = true;
        setPaymentPlans((prev) => ({ ...prev, result: updatedPlans }));
      } else {
        updateSelectedCards(row);
      }
    },
    [paymentPlans.result]
  );

  const handlePaymentPlanNotAvailableConfirm = useCallback(() => {
    if (!activeItem) return;

    const rowIndex = paymentPlans.result.findIndex(
      (item) => item.propertyPaymentPlanId === activeItem.propertyPaymentPlanId
    );

    if (rowIndex === -1) return;

    if (!activeItem.isSelectedUnit) {
      const updatedPlans = [...paymentPlans.result];
      updatedPlans[rowIndex].isSelectedUnit = true;
      setPaymentPlans((prev) => ({ ...prev, result: updatedPlans }));
    } else {
      updateSelectedCards(activeItem);
    }

    setIsPaymentPlanNotAvailableDialogOpen(false);
  }, [activeItem, paymentPlans.result, updateSelectedCards]);

  const onSelectAllClicked = useCallback(() => {
    const cardItems = [];
    if (!getIsSelectedAll()) {
      const updatedIds = [...checkedCardsIds];
      paymentPlans.result.forEach((item) => {
        if (!getIsSelected(item) || item.isSelected) {
          if (item.isSelected) {
            const updatedPlans = [...paymentPlans.result];
            const index = updatedPlans.findIndex(
              (p) => p.propertyPaymentPlanId === item.propertyPaymentPlanId
            );
            if (index !== -1) {
              updatedPlans[index].isSelected = false;
              setPaymentPlans((prev) => ({ ...prev, result: updatedPlans }));
            }
          }
          updatedIds.push(item.propertyPaymentPlanId);
          cardItems.push({ ...item });
        }
      });
      setCheckedCardsIds(updatedIds);
    } else {
      const updatedIds = [...checkedCardsIds];
      paymentPlans.result.forEach((item) => {
        if (getIsSelected(item)) {
          if (item.isSelected) {
            const updatedPlans = [...paymentPlans.result];
            const index = updatedPlans.findIndex(
              (p) => p.propertyPaymentPlanId === item.propertyPaymentPlanId
            );
            if (index !== -1) {
              updatedPlans[index].isSelected = false;
              setPaymentPlans((prev) => ({ ...prev, result: updatedPlans }));
            }
          }
          const isSelectedIndex = updatedIds.indexOf(
            item.propertyPaymentPlanId
          );
          if (isSelectedIndex !== -1) {
            updatedIds.splice(isSelectedIndex, 1);
          }
        }
      });
      setCheckedCardsIds(updatedIds);
    }
    setCheckedCards(cardItems);
  }, [paymentPlans.result, checkedCardsIds]);

  const onPageIndexChanged = useCallback((pageIndex) => {
    setFilter((prev) => ({ ...prev, pageIndex }));
  }, []);

  const getIsSelected = useCallback(
    (row) => {
      return (
        checkedCards.some(
          (item) => item.propertyPaymentPlanId === row.propertyPaymentPlanId
        ) || row.isSelected
      );
    },
    [checkedCards, currentRowClicked]
  );

  const getIsSelectedAll = useCallback(() => {
    return (
      checkedCardsIds.length > 0 &&
      paymentPlans.result.every((item) =>
        checkedCardsIds.includes(item.propertyPaymentPlanId)
      )
    );
  }, [checkedCardsIds, paymentPlans.result]);

  useEffect(() => {
    getAllPaymentTypes(unitId);
  }, [getAllPaymentTypes, unitId]);

  useEffect(() => {
    const selectedPlans = paymentPlans.result.filter(
      (item) => item.isSelectedUnit
    );
    setCheckedCards(selectedPlans);
    setCheckedCardsIds(selectedPlans.map((item) => item.propertyPaymentPlanId));
  }, [paymentPlans.result]);

  useEffect(() => {
    bottomBoxComponentUpdate(
      <div className='d-flex-v-center-h-end flex-wrap'>
        <ButtonBase
          className='btns theme-solid mb-2'
          onClick={updateUnitPaymentPlanApi}
        >
          <span>{t('Shared:save')}</span>
        </ButtonBase>
      </div>
    );
  }, [updateUnitPaymentPlanApi, t]);

  useEffect(() => {
    return () => bottomBoxComponentUpdate(null);
  }, []);

  return (
    <div className='view-wrapper activities-view-wrapper'>
      <Spinner isActive={isLoading} isAbsolute />

      <div className='d-flex-column mt-3'>
        <div className='w-100 px-3'>
          <Tables
            data={paymentPlans.result}
            headerData={[
              {
                id: 1,
                label: 'number',
                input: 'numberOfInstallment',
                cellClasses: (item) =>
                  item?.allowSelectPaymentPlan === false && isCorrectLocation()
                    ? 'payment-plan-disabled'
                    : '',
              },
              {
                id: 2,
                label: 'type',
                component: (item) => (
                  <span>
                    {(item &&
                      item.paymentTypeLookupDto &&
                      item.paymentTypeLookupDto.paymentTypeLookupName) ||
                      'N/A'}
                  </span>
                ),
                cellClasses: (item) =>
                  item?.allowSelectPaymentPlan === false && isCorrectLocation()
                    ? 'payment-plan-disabled'
                    : '',
              },
              {
                id: 3,
                label: 'amount',
                component: (item) => (
                  <span>
                    {(item &&
                      item.amountNatureLookup &&
                      item.amountNatureLookup.amountNatureLookupName) ||
                      'N/A'}
                  </span>
                ),
                cellClasses: (item) =>
                  item?.allowSelectPaymentPlan === false && isCorrectLocation()
                    ? 'payment-plan-disabled'
                    : '',
              },
              {
                id: 4,
                label: 'due',
                component: (item) => (
                  <span>
                    {(item &&
                      item.frequencyLookupDto &&
                      item.frequencyLookupDto.frequencyLookupName) ||
                      'N/A'}
                  </span>
                ),
                cellClasses: (item) =>
                  item?.allowSelectPaymentPlan === false && isCorrectLocation()
                    ? 'payment-plan-disabled'
                    : '',
              },
              {
                id: 5,
                label: 'description',
                component: (item) => (
                  <span>{(item.description && item.description) || 'N/A'}</span>
                ),
                cellClasses: (item) =>
                  item?.allowSelectPaymentPlan === false && isCorrectLocation()
                    ? 'payment-plan-disabled'
                    : '',
              },
            ]}
            defaultActions={[
              {
                enum: TableActions.editText.key,
                isDisabled: getIsAllowedPermission(
                  Object.values(LeadOwnerUnitSalePermissions),
                  loginResponse,
                  LeadOwnerUnitSalePermissions.EditPaymentPlanForUnit
                    .permissionsId
                ),
              },
              {
                enum: TableActions.deleteText.key,
                isDisabled: getIsAllowedPermission(
                  Object.values(LeadOwnerUnitSalePermissions),
                  loginResponse,
                  LeadOwnerUnitSalePermissions.DeletePaymentPlanForUnit
                    .permissionsId
                ),
              },
            ]}
            actionsOptions={{
              onActionClicked: tableActionClicked,
            }}
            selectAllOptions={{
              selectedRows: checkedCardsIds,
              getIsSelected,
              disabledRows: [],
              withCheckAll: true,
              onSelectAllClicked,
              isSelectAll: getIsSelectedAll(),
              onSelectClicked,
              checkBoxesClassName: isCorrectLocation()
                ? 'payment-plan-disabled'
                : '',
            }}
            HideSellectAll
            activePage={filter.pageIndex}
            itemsPerPage={filter.pageSize}
            totalItems={paymentPlans.totalCount}
            translationPath={translationPath}
            parentTranslationPath={parentTranslationPath}
            translationPathFordata={translationPath}
          />
        </div>
      </div>

      {openDialog && (
        <PaymentPlanManagementDialog
          open={openDialog}
          activeItem={activeItem}
          onSave={() => {
            setOpenDialog(false);
            onPageIndexChanged(0);
          }}
          close={() => setOpenDialog(false)}
          translationPath={translationPath}
          parentTranslationPath={parentTranslationPath}
        />
      )}

      {openConfirmDialog && (
        <PaymentPlanDeleteDialog
          isOpen={openConfirmDialog}
          activeItem={activeItem}
          reloadData={() => {
            setOpenConfirmDialog(false);
            onPageIndexChanged(0);
            getAllPaymentTypes(unitId);
          }}
          isOpenChanged={() => setOpenConfirmDialog(false)}
          translationPath={translationPath}
          parentTranslationPath={parentTranslationPath}
        />
      )}

      {isPaymentPlanNotAvailableDialogOpen && (
        <PaymentPlanNotAvailableDialog
          isOpen={isPaymentPlanNotAvailableDialogOpen}
          t={t}
          onSave={() => {
            handlePaymentPlanNotAvailableConfirm();
            setActiveItem(null);
          }}
          onClose={() => {
            setIsPaymentPlanNotAvailableDialogOpen(false);
            setActiveItem(null);
          }}
          translationPath={translationPath}
          parentTranslationPath={parentTranslationPath}
        />
      )}
    </div>
  );
};

UnitProfilePaymentPlanComponent.propTypes = {
  parentTranslationPath: PropTypes.string.isRequired,
  translationPath: PropTypes.string.isRequired,
};
